// Spelersprofielen: elke speler heeft een vaste ID, zodat statistieken blijven kloppen na een naamswijziging
// en twee spelers met dezelfde naam nooit door elkaar lopen.

export interface PlayerProfile {
  id: string;
  name: string;
  createdAt: number;
}

const PLAYERS_KEY = 'darts_pwa_players';
export const MAX_NAME_LENGTH = 15;

/** Vergelijkt namen zonder hoofdletters en dubbele spaties. */
export function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function cleanName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, MAX_NAME_LENGTH);
}

function newId(): string {
  try {
    return `pl-${crypto.randomUUID()}`;
  } catch {
    return `pl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function getProfiles(): PlayerProfile[] {
  try {
    const raw = localStorage.getItem(PLAYERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is PlayerProfile =>
        !!p && typeof p.id === 'string' && typeof p.name === 'string' && p.name.trim() !== ''
    );
  } catch {
    return [];
  }
}

function saveProfiles(profiles: PlayerProfile[]): void {
  try {
    localStorage.setItem(PLAYERS_KEY, JSON.stringify(profiles));
  } catch (e) {
    console.error('Failed to save players', e);
  }
}

export function findProfileByName(name: string, profiles = getProfiles()): PlayerProfile | undefined {
  const key = normalizeName(name);
  return profiles.find(p => normalizeName(p.name) === key);
}

/** Geeft per naam het bestaande profiel, of maakt er een aan. Volgorde blijft gelijk. */
export function ensureProfiles(names: string[], now = Date.now()): PlayerProfile[] {
  const profiles = getProfiles();
  let changed = false;
  const result = names.map(raw => {
    const name = cleanName(raw);
    const existing = findProfileByName(name, profiles);
    if (existing) return existing;
    const created: PlayerProfile = { id: newId(), name, createdAt: now };
    profiles.push(created);
    changed = true;
    return created;
  });
  if (changed) saveProfiles(profiles);
  return result;
}

export type RenameResult = 'ok' | 'empty' | 'taken' | 'missing';

/** Hernoemt een profiel; de statistieken blijven aan de ID hangen. */
export function renameProfile(id: string, newName: string): RenameResult {
  const name = cleanName(newName);
  if (!name) return 'empty';
  const profiles = getProfiles();
  const target = profiles.find(p => p.id === id);
  if (!target) return 'missing';
  const clash = findProfileByName(name, profiles);
  if (clash && clash.id !== id) return 'taken';
  target.name = name;
  saveProfiles(profiles);
  return 'ok';
}

/** Zoekt dubbele namen in een opstelling; geeft de eerste dubbele naam terug of null. */
export function findDuplicateName(names: string[]): string | null {
  const seen = new Set<string>();
  for (const name of names) {
    const key = normalizeName(name);
    if (seen.has(key)) return cleanName(name);
    seen.add(key);
  }
  return null;
}
